/** TimeEntriesFilters — filter bar for the list tab.
 *
 * Filters are owned by the parent (URL-synced); this is a controlled
 * component that emits the next filter object on every change. The shape
 * matches `useTimeEntries` params 1:1 so the parent can spread directly. */

import { Select, DatePicker, Space } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import type { ActionType } from "../../types";
import type { BillingStatus, RunStatus } from "./utils";
import { BILLING_STATUS_LABELS, RUN_STATUS_LABELS } from "./utils";

const { RangePicker } = DatePicker;

export interface TimeEntriesFilterValues {
  matterId?: string;
  employeeId?: string;
  actionTypeId?: string;
  status?: RunStatus;
  billingStatus?: BillingStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface TimeEntriesFiltersProps {
  value: TimeEntriesFilterValues;
  onChange: (next: TimeEntriesFilterValues) => void;
  matterOptions: { value: string; label: string }[];
  employeeOptions: { value: string; label: string }[];
  actionTypes: ActionType[];
}

export default function TimeEntriesFilters({
  value,
  onChange,
  matterOptions,
  employeeOptions,
  actionTypes,
}: TimeEntriesFiltersProps) {
  const dateRange: [Dayjs, Dayjs] | undefined =
    value.dateFrom && value.dateTo
      ? [dayjs(value.dateFrom), dayjs(value.dateTo)]
      : undefined;

  return (
    <div
      data-testid="time-entries-filters"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "var(--jerry-sp-2, 8px)",
        alignItems: "center",
        marginBottom: "var(--jerry-sp-4, 16px)",
      }}
    >
      <Space size="small" wrap>
        <Select
          data-testid="filter-matter"
          allowClear
          showSearch
          placeholder="事项"
          style={{ minWidth: 200 }}
          options={matterOptions}
          value={value.matterId}
          onChange={(v) => onChange({ ...value, matterId: v ?? undefined })}
          filterOption={(input, opt) =>
            (opt?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
          }
        />
        <Select
          data-testid="filter-action-type"
          allowClear
          placeholder="动作分类"
          style={{ minWidth: 140 }}
          value={value.actionTypeId}
          onChange={(v) => onChange({ ...value, actionTypeId: v ?? undefined })}
          options={actionTypes.map((a) => ({ value: a.id, label: a.name }))}
        />
        <Select
          data-testid="filter-billing"
          allowClear
          placeholder="计费状态"
          style={{ minWidth: 120 }}
          value={value.billingStatus}
          onChange={(v) =>
            onChange({ ...value, billingStatus: (v as BillingStatus) ?? undefined })
          }
          options={Object.entries(BILLING_STATUS_LABELS).map(([k, label]) => ({
            value: k,
            label,
          }))}
        />
        <Select
          data-testid="filter-run-status"
          allowClear
          placeholder="运行状态"
          style={{ minWidth: 120 }}
          value={value.status}
          onChange={(v) =>
            onChange({ ...value, status: (v as RunStatus) ?? undefined })
          }
          options={Object.entries(RUN_STATUS_LABELS).map(([k, label]) => ({
            value: k,
            label,
          }))}
        />
        <Select
          data-testid="filter-employee"
          allowClear
          showSearch
          placeholder="被记录员工"
          style={{ minWidth: 160 }}
          options={employeeOptions}
          value={value.employeeId}
          onChange={(v) => onChange({ ...value, employeeId: v ?? undefined })}
          filterOption={(input, opt) =>
            (opt?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
          }
        />
        <RangePicker
          data-testid="filter-date-range"
          value={dateRange}
          onChange={(range) => {
            if (!range || range.length !== 2 || !range[0] || !range[1]) {
              onChange({ ...value, dateFrom: undefined, dateTo: undefined });
              return;
            }
            onChange({
              ...value,
              dateFrom: range[0].startOf("day").toISOString(),
              dateTo: range[1].endOf("day").toISOString(),
            });
          }}
        />
      </Space>
    </div>
  );
}
